
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, AlertCircle } from "lucide-react";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";

interface CoingeckoSettingsProps {
  form: UseFormReturn<{
    coingeckoApiKey?: string;
    [key: string]: string | undefined;
  }>;
}

export const CoingeckoSettings = ({ form }: CoingeckoSettingsProps) => {
  const { toast } = useToast();
  const [testingCoingecko, setTestingCoingecko] = useState(false);
  const [coingeckoStatus, setCoingeckoStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const testCoingecko = async () => {
    const apiKey = form.getValues("coingeckoApiKey");
    
    if (!apiKey) {
      toast({
        title: "API Key Required",
        description: "Please enter a Coingecko API key before testing.",
        variant: "destructive",
      });
      return;
    }
    
    setTestingCoingecko(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const success = Math.random() > 0.3;
      
      if (success) {
        setCoingeckoStatus('success');
        toast({
          title: "Coingecko API Connection Successful",
          description: "Your API key is valid and working correctly.",
        });
      } else {
        setCoingeckoStatus('error');
        toast({
          title: "Coingecko API Connection Failed",
          description: "Unable to connect with the provided API key. Please check and try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setCoingeckoStatus('error');
      toast({
        title: "Coingecko API Test Failed",
        description: "An error occurred while testing the connection.",
        variant: "destructive",
      });
    } finally {
      setTestingCoingecko(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Coingecko API</CardTitle>
        <CardDescription>Configure your Coingecko API integration for cryptocurrency data.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <FormField
            control={form.control}
            name="coingeckoApiKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Coingecko API Key</FormLabel>
                <FormControl>
                  <Input {...field} type="password" placeholder="Enter your Coingecko API key" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Form>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button 
          variant="default" 
          onClick={testCoingecko} 
          disabled={testingCoingecko}
        >
          {testingCoingecko ? "Testing..." : "Test & Save Connection"}
          {coingeckoStatus === 'success' && <CheckCircle className="ml-2 h-4 w-4 text-green-500" />}
          {coingeckoStatus === 'error' && <AlertCircle className="ml-2 h-4 w-4 text-red-500" />}
        </Button>
      </CardFooter>
    </Card>
  );
};
